import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { ResponseError } from '@ais-ui/api-spec/shapes';
import * as Sentry from '@sentry/browser';

// Interface for tracking recent toasts
interface ToastRecord {
  status: number;
  timestamp: number;
  message: string;
}

// Configuration options for the API client wrapper
interface ApiClientWrapperOptions {
  toast?: ReturnType<typeof useToast>['toast'];
  messages?: ReturnType<typeof useLocalization>['messages'];
  deduplicationWindow?: number;
  showToasts?: boolean; // New parameter to control toasts (defaults to false)
}

export class ApiClientWrapper<T extends object> {
  private client: T;
  private toast?: ReturnType<typeof useToast>['toast'];
  private messages?: ReturnType<typeof useLocalization>['messages'];
  private recentToasts: ToastRecord[] = [];
  private deduplicationWindow = 10000; // 10 seconds default
  private maxToastHistory = 10; // Keep only last 10 toast records
  private showToasts = false; // Default to false (toasts off)

  constructor(client: T, options: ApiClientWrapperOptions = {}) {
    this.client = client;
    this.toast = options.toast;
    this.messages = options.messages;
    this.deduplicationWindow = options.deduplicationWindow ?? 10000;
    this.showToasts = options.showToasts ?? false; // Default to false
  }

  /**
   * Create a proxied version of the client with error handling
   */
  getProxiedClient(): T {
    return new Proxy(this.client, {
      get: (target, prop: string | symbol) => {
        const property = prop as keyof T;
        const originalValue = target[property];

        if (typeof originalValue === 'function') {
          return async (...args: any[]) => {
            try {
              // Call the original method on the client
              return await (originalValue as any).apply(target, args);
            } catch (error: any) {
              // Always log to Sentry
              this.logErrorToSentry(error, prop.toString(), args);

              // Show toast for specific status codes (if enabled and toast is available)
              if (this.showToasts && this.toast && this.messages) {
                this.showToastForError(error);
              }

              // Apply centralized error handling
              const parsedError = await this.parseErrorResponse(error);
              throw parsedError;
            }
          };
        }

        // Return other properties as-is
        return originalValue;
      },
    }) as T;
  }

  /**
   * Log error to Sentry with context
   */
  private logErrorToSentry(error: any, methodName: string, args: any[]): void {
    try {
      // Extract relevant error information with defensive checks
      const status =
        error instanceof ResponseError
          ? error.response?.status
          : error.response?.status;

      const url = this.extractUrl(error);
      const errorMessage = error?.message || 'Unknown API Error';

      // Create a more descriptive error for Sentry
      const sentryError = new Error(`API Error: ${errorMessage}`);
      sentryError.name = 'APIError';

      // Set Sentry context with additional details
      Sentry.withScope((scope) => {
        // Set error level based on status code
        if (status) {
          if (status >= 500) {
            scope.setLevel('error');
          } else if (status >= 400) {
            scope.setLevel('warning');
          } else {
            scope.setLevel('info');
          }
        }

        // Add tags for filtering
        scope.setTag('api.status', status || 'unknown');
        scope.setTag('api.method', methodName);
        if (url) {
          scope.setTag('api.endpoint', this.formatUrlForDisplay(url));
        }

        // Add context with more details
        scope.setContext('api_error', {
          status,
          url,
          method: methodName,
          timestamp: new Date().toISOString(),
          // Be careful not to log sensitive data in args
          argsCount: args?.length || 0,
        });

        // Add the original error as extra data with defensive checks
        const originalErrorContext: Record<string, any> = {};

        if (error?.message) {
          originalErrorContext.message = error.message;
        }

        if (error?.stack) {
          originalErrorContext.stack = error.stack;
        }

        if (error?.response) {
          originalErrorContext.response = {
            status: error.response?.status,
            statusText: error.response?.statusText,
            // Safely check for headers
            hasHeaders: !!error.response?.headers,
            // Avoid logging potentially sensitive response body
            hasBody: !!error.response?.body,
          };
        }

        // Only set context if we have data
        if (Object.keys(originalErrorContext).length > 0) {
          scope.setContext('original_error', originalErrorContext);
        }

        // Add breadcrumb for the API call
        Sentry.addBreadcrumb({
          category: 'api',
          message: `API call failed: ${methodName}`,
          level: status >= 500 ? 'error' : 'warning',
          data: {
            status,
            url,
            method: methodName,
          },
        });

        // Capture the error
        Sentry.captureException(sentryError);
      });
    } catch (sentryError) {
      // If Sentry logging fails, log to console but don't interrupt the flow
      console.error('Failed to log error to Sentry:', sentryError);
    }
  }

  /**
   * Clean up old toast records that are outside the deduplication window
   */
  private cleanupOldToasts(): void {
    const now = Date.now();
    // Remove toasts older than the deduplication window
    this.recentToasts = this.recentToasts.filter(
      (record) => now - record.timestamp < this.deduplicationWindow
    );

    // Also limit the size to prevent memory issues
    if (this.recentToasts.length > this.maxToastHistory) {
      this.recentToasts = this.recentToasts.slice(-this.maxToastHistory);
    }
  }

  /**
   * Check if a similar toast was recently shown
   */
  private wasRecentlyShown(status: number, message: string): boolean {
    this.cleanupOldToasts();

    const now = Date.now();
    return this.recentToasts.some(
      (record) =>
        record.status === status &&
        record.message === message &&
        now - record.timestamp < this.deduplicationWindow
    );
  }

  /**
   * Record that a toast was shown
   */
  private recordToast(status: number, message: string): void {
    this.recentToasts.push({
      status,
      message,
      timestamp: Date.now(),
    });
  }

  /**
   * Extract URL from error object
   */
  private extractUrl(error: any): string | undefined {
    // Try different common locations where the URL might be stored
    return (
      error.response?.url ||
      error.response?.config?.url ||
      error.config?.url ||
      error.url ||
      error.request?.responseURL ||
      error.request?.url
    );
  }

  /**
   * Format URL for display (optionally truncate or extract path)
   */
  private formatUrlForDisplay(url: string | undefined): string {
    if (!url) return '';

    try {
      // If it's a full URL, extract the path
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        // Return path + search params (without the domain)
        return urlObj.pathname + urlObj.search;
      }
      // If it's already a path, return as is
      return url;
    } catch {
      // If URL parsing fails, return the original
      return url;
    }
  }

  /**
   * Show toast messages for specific HTTP status codes
   */
  private showToastForError(error: any): void {
    if (!this.showToasts || !this.toast || !this.messages) return;

    // Handle ResponseError (HTTP status code errors)
    let status: number | undefined;

    if (error instanceof ResponseError) {
      status = error.response?.status;
    } else if (error.response?.status) {
      status = error.response.status;
    }

    if (!status) return;

    // Extract the URL if available
    const url = this.extractUrl(error);
    const formattedUrl = this.formatUrlForDisplay(url);
    const urlSuffix = formattedUrl ? ` (${formattedUrl})` : '';

    // Determine the toast configuration based on status
    let toastConfig: {
      title: string;
      description: string;
      variant: 'destructive';
    } | null = null;

    switch (true) {
      // 400 Bad Request
      case status === 400:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Bad Request',
          description: `The request was invalid. Please check your input and try again${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 401 Unauthorized
      case status === 401:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Unauthorized',
          description: `You are not authorized to perform this action. ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 403 Forbidden
      case status === 403:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Access Denied',
          description: `You do not have permission to access this resource ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 404 Not Found
      case status === 404:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Not Found',
          description: `The requested resource was not found ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 409 Conflict
      case status === 409:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Conflict',
          description: `There was a conflict with your request. The resource may have been modified ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 422 Unprocessable Entity
      case status === 422:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Validation Error',
          description: `The data provided is invalid. Please check your input ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 429 Too Many Requests
      case status === 429:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Rate Limited',
          description: `Too many requests. Please wait a moment before trying again ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // 5xx Server Errors
      case status >= 500 && status < 600:
        toastConfig = {
          title: this.messages.genericToastTitleError || 'Server Error',
          description: `A server error occurred. Please try again later ${urlSuffix}`,
          variant: 'destructive',
        };
        break;

      // Don't show toast for other status codes by default
      default:
        break;
    }

    // Only show the toast if we have config and it wasn't recently shown
    if (toastConfig) {
      const toastKey = `${toastConfig.title}|${toastConfig.description}`;

      if (!this.wasRecentlyShown(status, toastKey)) {
        this.toast(toastConfig);
        this.recordToast(status, toastKey);
      }
    }
  }

  /**
   * Parse the error response and extract useful information.
   */
  private async parseErrorResponse(error: any): Promise<any> {
    if (error.response?.body) {
      try {
        // First, check if the response has a content-type header
        const contentType = error.response.headers?.get?.('content-type') || '';

        // If it's JSON, parse it
        if (contentType.includes('application/json')) {
          const errorBody = await error.response.json();
          error.response.message = errorBody?.message || 'An error occurred';
          error.response.parsedBody = errorBody;
        } else {
          // Otherwise, try to get it as text
          const textBody = await error.response.text();

          // Check if it's HTML and try to extract a meaningful message
          if (
            contentType.includes('text/html') ||
            textBody.trim().startsWith('<')
          ) {
            // Extract text from HTML error pages
            const match = textBody.match(/<h1>([^<]+)<\/h1>/);
            const htmlError = match ? match[1] : 'Server error';

            error.response.message = htmlError;
            error.response.parsedBody = {
              message: htmlError,
              originalHtml: textBody,
              isHtmlError: true,
            };
          } else {
            // Plain text response
            error.response.message = textBody || 'An error occurred';
            error.response.parsedBody = { message: textBody };
          }
        }

        return error;
      } catch (parsingError) {
        // If parsing fails, try to handle the error more gracefully
        try {
          // Attempt to read as text as a fallback
          const textBody = await error.response.text();
          error.response.message = textBody || 'Failed to parse error response';
          error.response.parsedBody = { message: textBody };
        } catch (secondError) {
          // If even text parsing fails, return a generic message
          console.error('Failed to parse error response:', parsingError);
          error.response.message = 'Failed to parse error response';
        }

        return error;
      }
    }

    // If no response body exists, return the original error with additional info
    return error;
  }

  /**
   * Clear the toast history (useful for testing or manual reset)
   */
  public clearToastHistory(): void {
    this.recentToasts = [];
  }

  /**
   * Update the deduplication window
   */
  public setDeduplicationWindow(ms: number): void {
    this.deduplicationWindow = ms;
  }

  /**
   * Enable or disable toast notifications
   */
  public setShowToasts(show: boolean): void {
    this.showToasts = show;
  }
}

// Hook-based wrapper factory for easy use with React hooks
interface UseApiClientWrapperOptions {
  deduplicationWindow?: number;
  showToasts?: boolean; // Defaults to false
}

export const useApiClientWrapper = (
  options: UseApiClientWrapperOptions = {}
) => {
  const { toast } = useToast();
  const { messages } = useLocalization();

  return <T extends object>(client: T): T => {
    const wrapper = new ApiClientWrapper(client, {
      toast,
      messages,
      deduplicationWindow: options.deduplicationWindow,
      showToasts: options.showToasts ?? false, // Default to false
    });
    return wrapper.getProxiedClient();
  };
};
